'use server'

import { apiRequest, ApiResponse } from '../request'

export type MetaConnectionStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'REVOKED'

/**
 * OAUTH: login na Meta. PARTNER: pixel compartilhado como parceiro com a plataforma.
 * PIXEL_TOKEN: token gerado no Gerenciador de Eventos do pixel.
 */
export type MetaConnectionAuthMode = 'OAUTH' | 'PARTNER' | 'PIXEL_TOKEN'

export interface MetaConnectionSummary {
    connected: boolean
    authMode: MetaConnectionAuthMode | null
    status: MetaConnectionStatus | null
    metaUserId: string | null
    businessId: string | null
    adAccountId: string | null
    pixelId: string | null
    pageId: string | null
    /** Páginas que recebem leads de formulário (Lead Ads). */
    leadAdsPageIds: string[]
    scopes: string[]
    tokenExpiresAt: string | null
    capiTestEventCode: string | null
    lastError: string | null
}

export interface MetaTestEventResult {
    ok: boolean
    origin?: 'organization' | 'partner' | 'platform'
    pixelId?: string
    testEventCode?: string
    eventsReceived?: number
    fbtraceId?: string
    error?: string
}

export interface MetaNamedEntity {
    id: string
    name?: string
}

export interface MetaAdAccount extends MetaNamedEntity {
    accountId?: string
}

export interface MetaAssets {
    adAccounts: MetaAdAccount[]
    pixels: MetaNamedEntity[]
    pages: MetaNamedEntity[]
}

export interface MetaSelection {
    adAccountId?: string | null
    pixelId?: string | null
    pageId?: string | null
    leadAdsPageIds?: string[]
    capiTestEventCode?: string | null
}

/** URL do dialog OAuth (front faz window.location.href). */
export async function getMetaOAuthUrl(): Promise<ApiResponse<{ url: string }>> {
    return apiRequest<{ url: string }>('marketing/meta/oauth/url', {})
}

export async function getMetaConnection(): Promise<ApiResponse<MetaConnectionSummary>> {
    return apiRequest<MetaConnectionSummary>('marketing/meta/connection', {})
}

export async function getMetaAssets(): Promise<ApiResponse<MetaAssets>> {
    return apiRequest<MetaAssets>('marketing/meta/connection/assets', {})
}

export async function updateMetaSelection(
    selection: MetaSelection,
): Promise<ApiResponse<MetaConnectionSummary>> {
    return apiRequest<MetaConnectionSummary>('marketing/meta/connection', { data: selection }, 'patch')
}

export async function connectMetaPartner(data: {
    pixelId: string
    capiTestEventCode?: string | null
}): Promise<ApiResponse<MetaConnectionSummary>> {
    return apiRequest<MetaConnectionSummary>('marketing/meta/connection/partner', { data }, 'post')
}

/** Sem `accessToken`, o backend mantém o token já gravado. */
export async function connectMetaPixelToken(data: {
    pixelId: string
    accessToken?: string | null
    capiTestEventCode?: string | null
}): Promise<ApiResponse<MetaConnectionSummary>> {
    return apiRequest<MetaConnectionSummary>('marketing/meta/connection/pixel-token', { data }, 'post')
}

export async function sendMetaTestEvent(): Promise<ApiResponse<MetaTestEventResult>> {
    return apiRequest<MetaTestEventResult>('marketing/meta/connection/test-event', {}, 'post')
}

export async function disconnectMeta(): Promise<ApiResponse<{ disconnected: boolean }>> {
    return apiRequest<{ disconnected: boolean }>('marketing/meta/connection', {}, 'delete')
}

// ===================== Instagram Direct e Messenger =====================

export type MetaMessagingChannel = 'INSTAGRAM' | 'MESSENGER'

/** Página da conexão Meta, com a conta do Instagram ligada a ela (se houver). */
export interface MetaMessagingPage {
    id: string
    name?: string
    instagramAccountId: string | null
    instagramUsername: string | null
}

/** Página/conta do Instagram conectada às mensagens (sem token). */
export interface MetaMessagingAccount {
    id: number
    channel: MetaMessagingChannel
    pageId: string
    pageName: string | null
    instagramAccountId: string | null
    instagramUsername: string | null
    departmentId: number | null
    status: MetaConnectionStatus
    lastError: string | null
    createdAt: string
}

export async function getMetaMessagingPages(): Promise<ApiResponse<MetaMessagingPage[]>> {
    return apiRequest<MetaMessagingPage[]>('marketing/meta/messaging/pages', {})
}

export async function getMetaMessagingAccounts(): Promise<ApiResponse<MetaMessagingAccount[]>> {
    return apiRequest<MetaMessagingAccount[]>('marketing/meta/messaging/accounts', {})
}

export async function connectMetaMessagingAccount(data: {
    channel: MetaMessagingChannel
    pageId: string
    departmentId?: number | null
}): Promise<ApiResponse<MetaMessagingAccount>> {
    return apiRequest<MetaMessagingAccount>('marketing/meta/messaging/accounts', { data }, 'post')
}

export async function updateMetaMessagingDepartment(
    id: number,
    departmentId: number | null,
): Promise<ApiResponse<MetaMessagingAccount>> {
    return apiRequest<MetaMessagingAccount>(`marketing/meta/messaging/accounts/${id}`, { data: { departmentId } }, 'patch')
}

export async function disconnectMetaMessagingAccount(id: number): Promise<ApiResponse<{ disconnected: boolean }>> {
    return apiRequest<{ disconnected: boolean }>(`marketing/meta/messaging/accounts/${id}`, {}, 'delete')
}
